import React, { useEffect, useState, useRef } from 'react';
import { fetchApi } from '../services/api';
import { Plus, Radio, Trash2, ChevronRight, Link as LinkIcon, ExternalLink } from 'lucide-react';

export default function ChannelList({ userId, onSelectChannel }) {
  const [channels, setChannels] = useState([]);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelUrl, setNewChannelUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const addingRef = useRef(false);

  useEffect(() => {
    if (!userId) return;

    const fetchChannels = async () => {
      setLoading(true);
      try {
        const data = await fetchApi(`/channels/user/${userId}`);
        setChannels(data);
      } catch (err) {
        console.error('Kanallar alınamadı:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, [userId]);

  const handleAddChannel = async () => {
    if (!newChannelName.trim() || addingRef.current) return;

    addingRef.current = true;
    setAdding(true);
    try {
      const data = await fetchApi('/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newChannelName, url: newChannelUrl || undefined, userId }),
      });

      setNewChannelName('');
      setNewChannelUrl('');
      setChannels(prev => [...prev, data]);
    } catch (err) {
      console.error('Kanal ekleme hatası:', err);
      alert(err.message || 'Kanal eklenirken hata oluştu.');
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  };

  const handleDeleteChannel = async (channelId, channelName) => {
    const confirmDelete = window.confirm(
      `"${channelName}" kanalı ve tüm içeriği (gruplar, etkinlikler) silinecektir.\nEmin misiniz?`
    );
    if (!confirmDelete) return;

    try {
      await fetchApi(`/channels/${channelId}`, { method: 'DELETE' });
      setChannels(prev => prev.filter(c => c.id !== channelId));
    } catch (err) {
      console.error('Kanal silme hatası:', err);
      alert(err.message || 'Kanal silinirken hata oluştu.');
    }
  };

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '48px',
          height: '48px',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--primary-100)',
          marginBottom: '0.75rem',
        }}>
          <Radio size={24} style={{ color: 'var(--primary-500)' }} />
        </div>
        <h2 style={{
          fontSize: '1.375rem',
          fontWeight: '700',
          color: 'var(--text-primary)',
          margin: 0,
          letterSpacing: '-0.02em',
        }}>
          Kanallarım
        </h2>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '1rem',
          marginTop: '0.25rem',
        }}>
          Bir kanal seçin veya yeni kanal oluşturun
        </p>
      </div>

      {/* Add Channel */}
      <div style={{
        marginBottom: '1.25rem',
        background: 'var(--bg-elevated)',
        border: '1.5px solid var(--border-color)',
        borderRadius: 'var(--radius-md)',
        padding: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <input
            type="text"
            placeholder="Yeni kanal adı..."
            value={newChannelName}
            onChange={(e) => setNewChannelName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddChannel()}
            style={{
              flex: 1,
              padding: '0.7rem 0.85rem',
              borderRadius: 'var(--radius-sm)',
              border: '1.5px solid var(--border-color)',
              fontSize: '1rem',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              outline: 'none',
              transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--primary-400)';
              e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border-color)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <button
            onClick={handleAddChannel}
            disabled={adding}
            style={{
              padding: '0.7rem 1.15rem',
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: adding ? 'var(--neutral-300)' : 'var(--primary-600)',
              color: '#fff',
              fontWeight: '600',
              fontSize: '0.875rem',
              cursor: adding ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              transition: 'background var(--transition-fast)',
            }}
            onMouseEnter={(e) => !adding && (e.currentTarget.style.background = 'var(--primary-700)')}
            onMouseLeave={(e) => { if (!adding) e.currentTarget.style.background = 'var(--primary-600)'; }}
          >
            <Plus size={16} />
            {adding ? '...' : 'Oluştur'}
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <LinkIcon size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            type="url"
            placeholder="Kanal linki (opsiyonel) — https://..."
            value={newChannelUrl}
            onChange={(e) => setNewChannelUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddChannel()}
            style={{
              flex: 1,
              padding: '0.55rem 0.85rem',
              borderRadius: 'var(--radius-sm)',
              border: '1.5px solid var(--border-color)',
              fontSize: '0.875rem',
              background: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              outline: 'none',
              transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = 'var(--primary-400)';
              e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'var(--border-color)';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>

      {/* Channel List */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: 'var(--text-muted)',
          fontSize: '1rem',
        }}>
          Yükleniyor...
        </div>
      ) : channels.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 1rem',
          color: 'var(--text-muted)',
          fontSize: '1rem',
        }}>
          <Radio size={28} style={{ opacity: 0.4, marginBottom: '0.5rem' }} />
          <p>Henüz kanalınız yok. Yukarıdan yeni bir kanal oluşturun.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {channels.map((channel, index) => (
            <div
              key={channel.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                background: 'var(--bg-elevated)',
                border: '1.5px solid var(--border-color)',
                borderRadius: 'var(--radius-md)',
                padding: '1rem 1.15rem',
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                boxShadow: 'var(--shadow-sm)',
                animation: `fadeIn 0.3s ease-out ${index * 0.05}s both`,
              }}
              onClick={() => onSelectChannel(channel)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
              }}
            >
              <div style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--primary-50)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '0.85rem',
                flexShrink: 0,
              }}>
                <Radio size={16} style={{ color: 'var(--primary-500)' }} />
              </div>

              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div style={{
                  fontWeight: '600',
                  fontSize: '1rem',
                  color: 'var(--text-primary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {channel.name}
                </div>
                {channel.url && (
                  <a
                    href={channel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '3px',
                      fontSize: '0.75rem',
                      color: 'var(--primary-500)',
                      textDecoration: 'none',
                      marginTop: '2px',
                      fontWeight: '500',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <ExternalLink size={11} />
                    {channel.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </a>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteChannel(channel.id, channel.name); }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '5px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    color: 'var(--text-muted)',
                    transition: 'color var(--transition-fast)',
                  }}
                  title="Kanalı Sil"
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--danger-500)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                >
                  <Trash2 size={15} />
                </button>
                <ChevronRight size={18} style={{ color: 'var(--text-muted)' }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
